import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  title: string;
  description?: string;
  okLabel?: string;
  onClose: () => void;
}

export const AlertDialog = ({
  open,
  title,
  description,
  okLabel,
  onClose,
}: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const okRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Enter") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => okRef.current?.focus(), 50);
    return () => clearTimeout(id);
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="alert-title"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-label={t("common.close", "Fermer")}
        onClick={onClose}
        className="absolute inset-0 bg-ink/60 animate-fade-in"
      />
      <div className="relative w-full max-w-sm bg-bg rounded-modal shadow-warm-lg p-6 animate-scale-in">
        <div className="text-center text-3xl mb-2" aria-hidden="true">
          ⚠️
        </div>
        <h2
          id="alert-title"
          className="display-font text-lg font-semibold text-ink leading-tight text-center"
        >
          {title}
        </h2>
        {description && (
          <p className="mt-2 text-sm text-ink-soft leading-snug text-center">
            {description}
          </p>
        )}
        <button
          ref={okRef}
          type="button"
          onClick={onClose}
          className="mt-5 w-full bg-primary text-white rounded-button py-3 px-5 font-semibold shadow-warm transition-colors hover:bg-primary-hover"
        >
          {okLabel ?? t("common.ok", "OK")}
        </button>
      </div>
    </div>,
    document.body
  );
};
